'use client'

import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '../../../lib/utils'
import { Icon } from '..'
import { iconTypes } from '../Icon'
import { Button, ButtonProps } from '../Button'

const alertVariants = cva(
  'relative w-full rounded-lg border px-4 py-3 text-sm [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground [&>svg~*]:pl-7',
  {
    variants: {
      variant: {
        default: 'bg-background text-foreground',
        destructive:
          'border-destructive/50 bg-background text-destructive dark:border-destructive [&>svg]:text-destructive',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    role="alert"
    className={cn(alertVariants({ variant }), className)}
    {...props}
  />
))
Alert.displayName = 'Alert'

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn('mb-1 font-medium leading-none tracking-tight', className)}
    {...props}
  />
))
AlertTitle.displayName = 'AlertTitle'

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('text-sm [&_p]:leading-relaxed', className)}
    {...props}
  />
))
AlertDescription.displayName = 'AlertDescription'

export type AlertProps = {
  title: string
  description: string
  icon?: iconTypes
  action?: ButtonProps
} & React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof alertVariants>

export const AlertWrapper = (props: AlertProps) => {
  const { action, ...restprops } = props
  return (
    <Alert variant="default" {...restprops}>
      <div className="flex flex-row justify-between items-center">
        <div className="flex items-center gap-4">
          {props.icon && (
            <div className="shrink-0 w-4 h-4 flex items-center justify-center">
              <Icon name={props.icon} className="w-4 h-4" />
            </div>
          )}
          <div>
            <AlertTitle>{props.title}</AlertTitle>
            <AlertDescription>{props.description}</AlertDescription>
          </div>
        </div>
        {action && <Button {...props.action} />}
      </div>
    </Alert>
  )
}

export { Alert, AlertTitle, AlertDescription }
